import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import "./App.css";

// Public Pages
import Header from "./components/Header";
import Hero from "./components/Hero";
import About from "./components/About";
import Services from "./components/Services";
import Projects from "./components/Projects";
import Roadmap from "./components/Roadmap";
import Team from "./components/Team";
import Careers from "./components/Careers";
import Contact from "./components/Contact";
import Footer from "./components/Footer";

// Auth Pages
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

// Admin Pages (formerly CMS)
import CMSLayout from "./pages/CMS/CMSLayout";
import CMSDashboard from "./pages/CMS/Dashboard";
import CMSProjects from "./pages/CMS/Projects";
import CMSTeam from "./pages/CMS/Team";
import CMSCareers from "./pages/CMS/Careers";
import CMSServices from "./pages/CMS/Services";
import ContactSubmissions from "./pages/CMS/ContactSubmissions";
import QuoteRequests from "./pages/CMS/QuoteRequests";

// ERP Pages
import ERPLayout from "./pages/ERP/ERPLayout";
import ERPDashboard from "./pages/ERP/Dashboard";
import ERPProjects from "./pages/ERP/Projects/Projects";
import ERPLogin from "./pages/ERP/ERPLogin";
import ERPFinance from "./pages/ERP/Finance/Finance";
import ERPProcurement from "./pages/ERP/Procurement/Procurement";
import ERPSite from "./pages/ERP/Site/Site";
import ERPCRM from "./pages/ERP/CRM/CRM";
import ERPBOQ from "./pages/ERP/BOQ/BOQ";
import ERPContracts from "./pages/ERP/Contracts/Contracts";
import ERPResources from "./pages/ERP/Resources/Resources";
import ERPCommunication from "./pages/ERP/Communication/Communication";
import ERPDocuments from "./pages/ERP/Documents/Documents";
import ERPTasks from "./pages/ERP/Tasks/Tasks";

// Employee Pages
import EmployeeLayout from "./pages/Employee/EmployeeLayout";
import DashboardWrapper from "./pages/Employee/DashboardWrapper";
import EmployeeDashboard from "./pages/Employee/Dashboard";
import EmployeeLeaves from "./pages/Employee/Leaves";
import EmployeePayslips from "./pages/Employee/Payslips";
import EmployeeAttendance from "./pages/Employee/Attendance";
import CheckInOut from "./pages/Employee/CheckInOut";
import HRLeaveApprovals from "./pages/Employee/HRLeaves";
import HRDashboard from "./pages/Employee/HRDashboard";
import HREmployees from "./pages/Employee/HREmployees";
import HRPayroll from "./pages/Employee/HRPayroll";
import HRAttendance from "./pages/Employee/HRAttendance";
import HRAnnouncements from "./pages/Employee/HRAnnouncements";
import HRApplications from "./pages/Employee/HRApplications";
import HRPerformance from "./pages/Employee/HRPerformance";
import HRJobs from "./pages/Employee/HRJobs";
import HRERPAccess from "./pages/Employee/HRERPAccess";

// Homepage Component
const HomePage = () => {
  return (
    <>
      <Header />
      <Hero />
      <About />
      <Services />
      <Projects />
      <Roadmap />
      <Team />
      <Careers />
      <Contact />
      <Footer />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          
          {/* Auth Routes */}
          <Route path="/admin/login" element={<Login type="admin" />} />
          <Route path="/admin/forgot-password" element={<ForgotPassword type="admin" />} />
          <Route path="/employee/login" element={<Login type="employee" />} />
          <Route path="/employee/forgot-password" element={<ForgotPassword type="employee" />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Legacy CMS routes redirect to admin */}
          <Route path="/cms/login" element={<Navigate to="/admin/login" replace />} />
          
          {/* Admin Routes (Admin only) */}
          <Route path="/admin" element={<CMSLayout />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<CMSDashboard />} />
            <Route path="projects" element={<CMSProjects />} />
            <Route path="/admin/team" element={<CMSTeam />} />
            <Route path="/admin/careers" element={<CMSCareers />} />
            <Route path="/admin/services" element={<CMSServices />} />
            <Route path="/admin/contact-submissions" element={<ContactSubmissions />} />
            <Route path="/admin/quote-requests" element={<QuoteRequests />} />
            <Route path="/admin/applications" element={<div className="text-center py-12"><h2 className="text-2xl font-bold">Job Applications</h2><p className="text-gray-600 mt-2">View applications here</p></div>} />
            <Route path="/admin/inquiries" element={<div className="text-center py-12"><h2 className="text-2xl font-bold">Contact Inquiries</h2><p className="text-gray-600 mt-2">View inquiries here</p></div>} />
          </Route>
          
          {/* Employee Portal Routes (Employee & HR) */}
          <Route path="/employee" element={<EmployeeLayout />}>
            <Route index element={<Navigate to="/employee/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardWrapper />} />
            
            {/* Employee-only routes */}
            <Route path="checkin" element={<CheckInOut />} />
            <Route path="leaves" element={<EmployeeLeaves />} />
            <Route path="payslips" element={<EmployeePayslips />} />
            <Route path="attendance" element={<EmployeeAttendance />} />
            
            {/* HR routes */}
            <Route path="hr/employees" element={<HREmployees />} />
            <Route path="hr/leaves" element={<HRLeaveApprovals />} />
            <Route path="hr/attendance" element={<HRAttendance />} />
            <Route path="hr/payroll" element={<HRPayroll />} />
            <Route path="hr/jobs" element={<HRJobs />} />
            <Route path="hr/applications" element={<HRApplications />} />
            <Route path="hr/announcements" element={<HRAnnouncements />} />
            <Route path="hr/performance" element={<HRPerformance />} />
            <Route path="hr/erp-access" element={<HRERPAccess />} />
          </Route>

          {/* ERP System Routes (Separate Portal) */}
          <Route path="/erp" element={<ERPLayout />}>
            <Route index element={<Navigate to="/erp/dashboard" replace />} />
            <Route path="dashboard" element={<ERPDashboard />} />
            <Route path="projects" element={<ERPProjects />} />
            <Route path="boq" element={<ERPBOQ />} />
            <Route path="finance" element={<ERPFinance />} />
            <Route path="contracts" element={<ERPContracts />} />
            <Route path="procurement" element={<ERPProcurement />} />
            <Route path="resources" element={<ERPResources />} />
            <Route path="site-management" element={<ERPSite />} />
            <Route path="crm" element={<ERPCRM />} />
            <Route path="communication" element={<ERPCommunication />} />
            <Route path="documents" element={<ERPDocuments />} />
            <Route path="tasks" element={<ERPTasks />} />
          </Route>

          {/* Auth Routes */}
          <Route path="/admin/login" element={<Login type="admin" />} />
          <Route path="/admin/forgot-password" element={<ForgotPassword type="admin" />} />
          <Route path="/employee/login" element={<Login type="employee" />} />
          <Route path="/employee/forgot-password" element={<ForgotPassword type="employee" />} />
          <Route path="/erp/login" element={<ERPLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

